import { Link } from "react-router-dom";
import { Star, Eye, Heart, ShieldCheck } from "lucide-react";
import { formatDownloads } from "@/lib/api";
import { APP_CARD } from "@/constants/testIds";

export function AppCard({ app, size = "md" }) {
  const sizes = {
    sm: "w-full",
    md: "w-full",
    lg: "w-full",
  };
  return (
    <Link
      to={`/app/${app.slug}`}
      data-testid={`${APP_CARD}-${app.slug}`}
      className={`group block ${sizes[size]} relative`}
    >
      <div className="glass rounded-2xl p-4 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-[#00F0FF]/60 hover:shadow-[0_0_30px_rgba(0,240,255,0.15)]">
        <div className="flex items-start gap-3">
          <div className="relative w-16 h-16 rounded-xl overflow-hidden ring-1 ring-white/10 flex-shrink-0">
            {app.icon_url ? (
              <img src={app.icon_url} alt="" className="w-full h-full object-cover" loading="lazy" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#00F0FF]/20 to-[#39FF14]/20 flex items-center justify-center font-heading font-black text-2xl text-white/60">
                {app.name?.[0] || "?"}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h3 className="font-heading font-bold text-base truncate">{app.name}</h3>
              {app.developer_verified && (
                <ShieldCheck className="w-3.5 h-3.5 text-[#00F0FF] flex-shrink-0" />
              )}
            </div>
            <p className="text-white/50 text-xs truncate">{app.developer}</p>
            <div className="flex items-center gap-3 mt-2 mono text-[10px] text-white/60">
              <span className="flex items-center gap-1" data-testid={`${APP_CARD}-rating-${app.slug}`}>
                <Star className="w-3 h-3 fill-[#FFE300] text-[#FFE300]" />
                {app.rating.toFixed(1)}
              </span>
              <span className="flex items-center gap-1" data-testid={`${APP_CARD}-views-${app.slug}`}>
                <Eye className="w-3 h-3" />
                {formatDownloads(app.views || 0)}
              </span>
              <span className="flex items-center gap-1" data-testid={`${APP_CARD}-favorites-${app.slug}`}>
                <Heart className="w-3 h-3" />
                {formatDownloads(app.favorites || 0)}
              </span>
              <span
                className={`ml-auto px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest ${
                  app.is_free ? "bg-[#39FF14]/10 text-[#39FF14]" : "bg-[#FFE300]/10 text-[#FFE300]"
                }`}
              >
                {app.is_free ? "Free" : `$${app.price}`}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

export function AppCardCompact({ app, index }) {
  return (
    <Link
      to={`/app/${app.slug}`}
      data-testid={`${APP_CARD}-compact-${app.slug}`}
      className="group flex items-center gap-3 py-2 px-1 rounded-lg hover:bg-white/[0.03] transition-colors"
    >
      {typeof index === "number" && (
        <div className="mono text-2xl font-bold text-white/20 group-hover:text-[#00F0FF] w-8 transition-colors">
          {String(index + 1).padStart(2, "0")}
        </div>
      )}
      <img src={app.icon_url} alt="" className="w-12 h-12 rounded-lg object-cover ring-1 ring-white/10" loading="lazy" />
      <div className="min-w-0 flex-1">
        <div className="font-heading font-bold text-sm truncate">{app.name}</div>
        <div className="text-xs text-white/50 truncate">{app.tagline}</div>
      </div>
      <div className="mono text-[10px] text-white/60 flex items-center gap-1 flex-shrink-0">
        <Star className="w-3 h-3 fill-[#FFE300] text-[#FFE300]" />
        {app.rating.toFixed(1)}
      </div>
    </Link>
  );
}
